// Medical Portal — one Sample Concern (TL-CSD-SCR)
//
// The record as TrustLab logged it (locked server time stamp, ward, staff,
// issues, action taken, photos), the TAT box, the whole conversation trail
// and the hospital's reply form. Every message is stamped by Trust OPM and
// cannot be edited; a closed concern takes no further messages.
import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { MP, MONO, DISPLAY, Pill } from './shared';
import { useAuth } from '../../../context/AuthContext';
import { useGetSampleConcernQuery, useReplySampleConcernMutation } from '../../../store/queriesApi';

const REPLY_TYPES = ['Acknowledged', 'Clarification', 'Disputed', 'Corrective action taken'];
const STATUS_TONE = { Awaiting: 'amber', Replied: 'blue', Disputed: 'red', Closed: 'green' };
const STATUS_LABEL = { Awaiting: 'Awaiting your reply', Replied: 'You replied', Disputed: 'Disputed', Closed: 'Closed' };

// OPM stamps are India time without a zone: "2026-10-31 10:42:08".
export const stamp = (s) => {
  if (!s) return '—';
  const [d, t] = String(s).split(' ');
  const [y, m, day] = (d || '').split('-');
  return y ? `${day}-${m}-${y} ${(t || '').slice(0, 8)}` : String(s);
};
const hm = (s) => (s ? String(s).slice(11, 16) : '');
export const dur = (m) => {
  if (m == null) return '—';
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
  return `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;
};
const CLS_COLOR = { g: MP.green, a: MP.amber, r: MP.red, p: MP.textMuted };

// ── TAT trail: raised → hospital reply → closed ─────────────────────────
export const TatTrail = ({ r }) => {
  const t = r.tat || {};
  const replied = !!r.firstReplyAt;
  const closed = !!r.closedAt;
  const Step = ({ done, now, label }) => (
    <Stack alignItems="center" spacing={0.25} sx={{ flex: 'none' }}>
      <Box sx={{
        width: 12, height: 12, borderRadius: '50%',
        border: `2px solid ${done ? MP.tealDark : now ? MP.amber : MP.border}`,
        bgcolor: done ? MP.tealDark : now ? MP.amberLight : MP.white,
      }} />
      <Typography sx={{ fontFamily: MONO, fontSize: 10, color: MP.textMuted, whiteSpace: 'nowrap' }}>{label}</Typography>
    </Stack>
  );
  const Seg = ({ cls, text }) => (
    <Stack alignItems="center" spacing={0.25} sx={{ flex: '1 1 0', minWidth: 34, mb: '14px' }}>
      <Typography sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: CLS_COLOR[cls] || MP.textMuted, whiteSpace: 'nowrap' }}>{text}</Typography>
      <Box sx={{
        height: 4, width: '100%', borderRadius: 2,
        background: cls === 'p' ? `repeating-linear-gradient(90deg, ${MP.border} 0 4px, transparent 4px 8px)` : CLS_COLOR[cls] || MP.border,
      }} />
    </Stack>
  );
  return (
    <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }} title={`Reply target ${t.respH || 2} h · closure target ${t.closeH || 24} h`}>
      <Step done label={hm(r.raisedAt)} />
      <Seg cls={t.respCls} text={`${dur(t.respMin)}${replied ? '' : ' …'}`} />
      <Step done={replied} now={!replied} label={replied ? hm(r.firstReplyAt) : 'Reply'} />
      <Seg cls={replied ? t.closeCls : 'p'} text={replied ? `${dur(t.stage2Min)}${closed ? '' : ' …'}` : '—'} />
      <Step done={closed} now={replied && !closed} label={closed ? hm(r.closedAt) : 'Close'} />
    </Stack>
  );
};

const Field = ({ label, value, mono }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 10.5, fontFamily: MONO, fontWeight: 700, letterSpacing: '0.08em', color: MP.textMuted, textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, color: MP.dark, fontWeight: 600, fontFamily: mono ? MONO : undefined, wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);
const Section = ({ title, hint, children, sx }) => (
  <Box sx={{ border: `1px solid ${MP.border}`, borderRadius: '6px', bgcolor: MP.white, boxShadow: MP.shadow, overflow: 'hidden', mb: 2.5, ...sx }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.25, py: 1.25, borderBottom: `1px solid ${MP.border}` }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: MONO, letterSpacing: '0.1em', color: MP.textMuted, textTransform: 'uppercase' }}>{title}</Typography>
      {hint && <Typography sx={{ fontSize: 11.5, color: MP.textMuted }}>{hint}</Typography>}
    </Stack>
    {children}
  </Box>
);

const SampleConcernModal = ({ no, open, onClose }) => {
  const { user } = useAuth();
  const { data, isFetching, isError, error } = useGetSampleConcernQuery({ no }, { skip: !open || !no });
  const r = data?.record;
  const t = r?.tat || {};

  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [type, setType] = useState(REPLY_TYPES[0]);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState('');
  const [send, { isLoading: sending }] = useReplySampleConcernMutation();
  const trailRef = useRef(null);

  useEffect(() => {
    if (open) { setName(user?.name || ''); setRole(''); setType(REPLY_TYPES[0]); setText(''); setFiles([]); setErr(''); }
  }, [open, no, user]);
  useEffect(() => { if (trailRef.current) trailRef.current.scrollTop = trailRef.current.scrollHeight; }, [r]);

  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);

  const submit = async () => {
    setErr('');
    if (!name.trim() || !role.trim() || !text.trim()) { setErr('Add your name, designation and message before sending.'); return; }
    const fd = new FormData();
    fd.append('name', name.trim());
    fd.append('role', role.trim());
    fd.append('type', type);
    fd.append('text', text.trim());
    files.forEach((f) => fd.append('photos', f, f.name));
    try {
      await send({ no, formData: fd }).unwrap();
      setText(''); setFiles([]);
    } catch (e) {
      setErr(e?.data?.message || 'Could not send your reply.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper"
      PaperProps={{ sx: { borderRadius: '6px', bgcolor: MP.white, maxHeight: '92vh' } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 3, py: 2.25, borderBottom: `1px solid ${MP.border}` }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: MP.tealDark }}>{no}</Typography>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 700, color: MP.dark, lineHeight: 1.25, mt: 0.25 }}>
            {r ? r.issues.join(' · ') : 'Sample concern'}
          </Typography>
          {r && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Pill tone={STATUS_TONE[r.status] || 'grey'}>{STATUS_LABEL[r.status] || r.status}</Pill>
              <Pill tone={r.severity === 'Major' ? 'red' : 'amber'}>{r.severity}</Pill>
            </Stack>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ p: 3, bgcolor: MP.bg }}>
        {!r ? (
          <Typography sx={{ fontSize: 13, color: MP.textMuted, py: 4, textAlign: 'center' }}>
            {isError ? (error?.data?.message || 'Could not load this concern.') : 'Loading…'}
          </Typography>
        ) : (
          <>
            {/* The record */}
            <Section title="The record" hint="logged by TrustLab · locked">
              <Box sx={{ p: 2.25, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Field label="Time stamp (server)" value={stamp(r.raisedAt)} mono />
                <Field label="Received at" value={r.location} />
                <Field label="Raised by" value={r.raisedBy} />
                <Field label="Ward / department" value={r.ward} />
                <Field label="Handed over by" value={r.handedBy} />
                <Field label="Samples" value={String(r.count)} />
                <Field label="Reference" value={r.ref || 'None — unlabelled'} mono />
                {r.patient?.name && <Field label="Patient (LIS)" value={`${r.patient.name}${r.patient.uhid ? ` · ${r.patient.uhid}` : ''}`} />}
                <Field label="Action on sample" value={r.action} />
              </Box>
              <Box sx={{ px: 2.25, pb: 2.25 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: MONO, letterSpacing: '0.1em', color: MP.textMuted, mb: 0.75 }}>DESCRIPTION</Typography>
                <Typography sx={{ fontSize: 13.5, color: MP.text, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{r.description}</Typography>
              </Box>
            </Section>

            {/* TAT */}
            <Section title="TAT trail" hint={`your first reply within ${t.respH || 2} h · closed within ${t.closeH || 24} h`}>
              <Box sx={{ p: 2.25 }}>
                <TatTrail r={r} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mt: 1.5 }}>
                  {[
                    ['Your first reply', dur(t.respMin), t.respCls, r.firstReplyAt ? `Replied ${stamp(r.firstReplyAt)}` : 'No reply yet'],
                    ['Reply to closure', dur(t.stage2Min), 'p', r.closedAt ? `Closed ${stamp(r.closedAt)}` : 'Open'],
                    ['Total', dur(t.closeMin), t.closeCls, `${r.messages} message${r.messages === 1 ? '' : 's'}`],
                  ].map(([l, v, c, s]) => (
                    <Box key={l}>
                      <Typography sx={{ fontSize: 11.5, color: MP.textMuted }}>{l}</Typography>
                      <Typography sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: CLS_COLOR[c] || MP.dark }}>{v}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: MP.textMuted }}>{s}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Section>

            {/* Photos */}
            <Section title={`Photographic evidence · ${r.photos.length}`} hint="stamped with time and place by TrustLab">
              <Box sx={{ p: 2.25, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1 }}>
                {r.photos.map((p, i) => (
                  <Box key={p.url || i} component="a" href={p.url} target="_blank" rel="noopener noreferrer">
                    <Box component="img" src={p.url} alt={`Evidence photo ${i + 1}`}
                      sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${MP.border}`, display: 'block' }} />
                  </Box>
                ))}
              </Box>
            </Section>

            {/* Conversation */}
            <Section title="Conversation trail" hint="every message time-stamped by the server and locked">
              <Box ref={trailRef} sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, maxHeight: 420, overflowY: 'auto', bgcolor: '#EEF2EF' }}>
                {r.trail.map((m, i) => (m.kind === 'sys' ? (
                  <Typography key={i} sx={{ alignSelf: 'center', textAlign: 'center', fontFamily: MONO, fontSize: 11, color: MP.textMuted, maxWidth: '90%' }}>
                    {m.text} · {stamp(m.at)}
                  </Typography>
                ) : (
                  <Box key={i} sx={{
                    alignSelf: m.side === 'partner' ? 'flex-end' : 'flex-start', maxWidth: '82%',
                    bgcolor: m.side === 'partner' ? MP.tealLight : MP.white,
                    border: `1px solid ${m.side === 'partner' ? `${MP.teal}33` : MP.border}`,
                    borderRadius: '10px', px: 1.5, py: 1.1, display: 'grid', gap: 0.5,
                  }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: m.side === 'partner' ? MP.tealDark : MP.dark }}>
                        {m.side === 'partner' ? `${m.name} (you)` : m.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: MP.textMuted }}>· {m.role}</Typography>
                      <Pill tone={m.type === 'Disputed' ? 'red' : m.type === 'More information needed' ? 'amber' : m.side === 'partner' ? 'teal' : 'grey'}>{m.type}</Pill>
                    </Stack>
                    <Typography sx={{ fontSize: 13, color: MP.text, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
                    {m.photos?.length > 0 && (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {m.photos.map((p, j) => (
                          <Box key={j} component="a" href={p.url} target="_blank" rel="noopener noreferrer">
                            <Box component="img" src={p.url} alt="Attached photo" sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: '6px', border: `1px solid ${MP.border}` }} />
                          </Box>
                        ))}
                      </Stack>
                    )}
                    <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: MP.textMuted }}>{stamp(m.at)} · locked</Typography>
                  </Box>
                )))}
              </Box>

              {r.status === 'Closed' ? (
                <Typography sx={{ fontFamily: MONO, fontSize: 12, color: MP.textMuted, textAlign: 'center', p: 1.5, borderTop: `1px dashed ${MP.border}` }}>
                  🔒 Concern closed by TrustLab{r.closedBy ? ` (${r.closedBy})` : ''} on {stamp(r.closedAt)}. The trail is locked.
                </Typography>
              ) : (
                <Box sx={{ p: 2.25, borderTop: `1px solid ${MP.border}`, display: 'grid', gap: 1.5 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: MONO, letterSpacing: '0.1em', color: MP.textMuted }}>YOUR REPLY</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                    <TextField size="small" label="Your name" required value={name} onChange={(e) => setName(e.target.value)} />
                    <TextField size="small" label="Designation / ward" required placeholder="e.g. Nursing In-charge, ICU" value={role} onChange={(e) => setRole(e.target.value)} />
                    <TextField size="small" select label="Type of reply" value={type} onChange={(e) => setType(e.target.value)}>
                      {REPLY_TYPES.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                    </TextField>
                    <Button variant="outlined" component="label" sx={{ textTransform: 'none', fontWeight: 600, borderColor: MP.border, color: MP.dark }}>
                      {files.length ? `${files.length} photo${files.length === 1 ? '' : 's'} attached` : 'Attach photos (optional)'}
                      <input hidden type="file" accept="image/*" multiple
                        onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files || [])].slice(0, 10)); e.target.value = ''; }} />
                    </Button>
                  </Box>
                  <TextField multiline minRows={3} size="small" label="Message" required
                    placeholder="Your response to TrustLab on this concern" value={text} onChange={(e) => setText(e.target.value)} />
                  {previews.length > 0 && (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {previews.map((src, i) => (
                        <Box key={src} sx={{ position: 'relative' }}>
                          <Box component="img" src={src} alt="" sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '6px', border: `1px solid ${MP.border}` }} />
                          <Box component="button" type="button" aria-label="Remove photo"
                            onClick={() => setFiles((f) => f.filter((_, j) => j !== i))}
                            sx={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 0, bgcolor: 'rgba(0,0,0,.65)', color: '#fff', cursor: 'pointer', fontSize: 12, lineHeight: '20px', p: 0 }}>×</Box>
                        </Box>
                      ))}
                    </Stack>
                  )}
                  {err && <Typography sx={{ fontSize: 12.5, color: MP.red, fontWeight: 600 }}>{err}</Typography>}
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1}>
                    <Typography sx={{ fontSize: 11.5, color: MP.textMuted }}>Each message is time-stamped by the server and cannot be edited once sent.</Typography>
                    <Button variant="contained" disabled={sending} onClick={submit}
                      sx={{ textTransform: 'none', fontWeight: 600, bgcolor: MP.teal, '&:hover': { bgcolor: MP.tealDark } }}>
                      {sending ? 'Sending…' : 'Send reply'}
                    </Button>
                  </Stack>
                </Box>
              )}
            </Section>

            {/* Fingerprint */}
            <Section title="Record fingerprint" hint="SHA-256 of the record, its photos and its trail" sx={{ mb: 0 }}>
              <Typography sx={{ m: 2.25, fontFamily: MONO, fontSize: 11, color: MP.textMuted, wordBreak: 'break-all', bgcolor: MP.bg, p: 1.25, borderRadius: '6px' }}>
                SHA-256 {r.fingerprint}
              </Typography>
            </Section>
            {isFetching && <Typography sx={{ fontSize: 11, color: MP.teal, mt: 1 }}>updating…</Typography>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SampleConcernModal;
